import { ReactElement, useEffect, useMemo } from 'react'
import { useLatest } from 'react-use'
import { sortBy, toNumber } from 'lodash-es'
import { FormControl, MenuItem, Select, Stack, Typography } from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { useCameraStore } from '@modugen/ui-toolkit'
import { MagicButton } from '@ui/actions'
import { roofStoreyKey, useControlStore, useModelStore } from '@editorStores'
import { useSceneElement } from '@editorHooks'
import WallConnectionProposalMesh from './WallConnectionProposalMesh'
import useApplyWallConnectionProposals from './useApplyWallConnectionProposals'
import { computeWallConnectionProposals } from './utils'

/**
 * "Automatisch" tool of the wall edit steps: storey dropdown plus an automatically computed
 * proposal for connecting walls of that storey whose ends stop short of (or reach past) a
 * neighbouring wall. The proposal is displayed in the scene as a dark-blue overlay floorplan mesh
 * (see `WallConnectionProposalMesh`) and can be persisted with "Annehmen", which updates the
 * affected walls one by one (walls already matching their proposal are skipped).
 */
const IFCWallAutoConnectSection = (): ReactElement => {
  const storey = useControlStore(state => state.floorplanState.storey)
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)
  const setIsOrthographic = useCameraStore(state => state.setIsOrthographic)
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const setVisibleStoreys = useModelStore(state => state.setVisibleStoreys)
  const walls = useModelStore(state => state.model.walls)

  const storeyOptions = useMemo(
    () =>
      sortBy(
        Array.from(availableStoreys).filter(availableStorey => availableStorey !== roofStoreyKey),
        toNumber,
      ),
    [availableStoreys],
  )

  // The tool does not change the view on its own: only when a storey is already shown on entry is
  // the storey visibility focused on it (so just its walls are displayed next to the proposal) —
  // otherwise nothing happens until one is picked in the dropdown. The view and visibility
  // deliberately persist on leave so switching between the tools doesn't reset the selected storey.
  const storeyLatest = useLatest(storey)
  useEffect(() => {
    if (storeyLatest.current) setVisibleStoreys([storeyLatest.current])
  }, [storeyLatest, setVisibleStoreys])

  const proposalWalls = useMemo(() => {
    if (!storey) return []
    return computeWallConnectionProposals(walls.filter(wall => wall.storey === storey))
  }, [walls, storey])

  const properties = useMemo(() => ({ proposalWalls }), [proposalWalls])

  useSceneElement({
    enabled: proposalWalls.length > 0,
    properties,
    component: WallConnectionProposalMesh,
  })

  const { applyProposals, isApplying } = useApplyWallConnectionProposals()

  return (
    <Stack spacing={1}>
      <FormControl size="small" fullWidth>
        <Select
          size="small"
          variant="outlined"
          value={storey ?? ''}
          displayEmpty
          data-cy="select-ifc-wall-auto-connect-storey"
          sx={{ '& .MuiSelect-select': { py: 0.25, fontSize: '0.75rem' } }}
          onChange={event => {
            const value = event.target.value
            if (!value) return
            // Switching here behaves like the storey controls' "Aktives SW und Filter wechseln":
            // the picked storey becomes the active (and only visible) one, in the floorplan view.
            setFloorplanStorey(value)
            setVisibleStoreys([value])
            setIsOrthographic(true)
          }}
          renderValue={value =>
            value ? (
              storeyToLabel(value)
            ) : (
              <Typography component="span" color="text.secondary">
                Stockwerk auswählen
              </Typography>
            )
          }
        >
          {storeyOptions.map(option => (
            <MenuItem key={option} value={option}>
              {storeyToLabel(option)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="body2" color="text.secondary">
        {!storey
          ? 'Wählen Sie ein Stockwerk aus, Wände automatisch zu verschneiden.'
          : proposalWalls.length > 0
          ? `${proposalWalls.length} ${
              proposalWalls.length === 1 ? 'Wand kann' : 'Wände können'
            } verlängert oder gekürzt werden, um eine Verbindung herzustellen. Der Vorschlag ist im Grundriss dunkelblau dargestellt.`
          : 'Keine Verbindungsvorschläge in diesem Stockwerk — alle Wandenden sind verbunden oder mehr als 30 cm von der nächsten Wand entfernt.'}
      </Typography>
      <MagicButton
        variant="contained"
        size="small"
        fullWidth
        onClick={() => applyProposals(proposalWalls)}
        disabled={proposalWalls.length === 0}
        loading={isApplying}
        data-cy="btn-ifc-wall-auto-connect-apply"
      >
        Annehmen
      </MagicButton>
    </Stack>
  )
}

export default IFCWallAutoConnectSection
